import {
	type AttachmentFile,
	type AttachmentId,
	Dtos,
	defaultAttachmentTypeLabelMap,
} from "@inrev/common";
import { type DataNeeded } from "@portal/api/decisioning";
import { DateTime } from "luxon";
import { formatDecisioningDate } from "../../decisioningLabels";

export const attachmentFileName = ({ file }: AttachmentFile) => `${file.name}.${file.extension}`;

const attachmentTypeLabel = ({ file }: AttachmentFile) =>
	file.types.length === 0
		? "Unclassified"
		: file.types.map((type) => defaultAttachmentTypeLabelMap[type] ?? type).join(", ");

export const attachmentOptionLabel = (attachment: AttachmentFile) =>
	[
		attachmentFileName(attachment),
		attachmentTypeLabel(attachment),
		formatDecisioningDate(DateTime.fromMillis(attachment.createdAtMs)),
	].join(" · ");

/**
 * Attachments that could satisfy a Document request: classified as any of its file types and
 * uploaded after the request was made. `null` for a Question or Task, which names no file type, so
 * nothing narrows it.
 */
export const matchingAttachments = (
	item: DataNeeded,
	attachments: readonly AttachmentFile[],
): AttachmentFile[] | null => {
	const fileTypes = new Set<string>(item.fileTypes);
	if (fileTypes.size === 0) return null;
	const requestedAtMs = item.createdAt.toMillis();
	return attachments.filter(
		(attachment) =>
			attachment.createdAtMs > requestedAtMs &&
			attachment.file.types.some((candidate) => fileTypes.has(candidate)),
	);
};

/** Attachments of the types a Document request asks for that arrived after the request was made. */
export const prefilledAttachmentIds = (
	item: DataNeeded,
	attachments: readonly AttachmentFile[],
): AttachmentId[] => (matchingAttachments(item, attachments) ?? []).map(({ id }) => id);

export const resolveIssue = (
	type: Dtos.Decisioning.DataNeededType,
	{ attachmentCount, answer }: { attachmentCount: number; answer: string },
): string | null => {
	if (type === "Document" && attachmentCount === 0) return "Pick at least one attachment.";
	if (type === "Question" && answer.trim() === "") return "Answer the question to continue.";
	return null;
};

export const toReceivedResolution = (
	type: Dtos.Decisioning.DataNeededType,
	{ attachmentIds, answer }: { attachmentIds: AttachmentId[]; answer: string },
): Dtos.Decisioning.DataNeeded.Resolve.Request => {
	switch (type) {
		case "Document":
			return { status: "received", attachmentIds };
		case "Question":
			return { status: "received", attachmentIds, answer: answer.trim() };
		case "Task":
			return { status: "received", attachmentIds: [] };
	}
};
